import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { howItWorks, walletOptions } from '../mock';

const StepButton = ({ step, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-5 rounded-xl border transition-all ${active ? 'border-primary bg-primary/5 shadow-sm' : 'border-border bg-card hover:border-primary/30'}`}
  >
    <div className="flex items-start gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-mono text-sm font-bold ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
        {step.num}
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-foreground">{step.title}</h3>
        <p className="mt-1.5 text-[15px] leading-relaxed text-muted-foreground">{step.desc}</p>
      </div>
    </div>
  </button>
);

const WalletPanel = ({ activeStep }) => (
  <div className="rounded-2xl border border-border bg-card p-6 lg:p-8 shadow-xl shadow-slate-900/5">
    <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Workspace preview</div>
    <div className="rounded-xl border border-border bg-muted/30 p-5 mb-5">
      <div className="flex items-center justify-between mb-4">
        <span className="font-display font-semibold text-foreground">Step {activeStep.num}</span>
        <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-mono text-primary">controlled</span>
      </div>
      <h4 className="font-display text-2xl font-bold text-foreground mb-2">{activeStep.title}</h4>
      <p className="text-muted-foreground leading-relaxed">{activeStep.desc}</p>
    </div>
    <div className="space-y-3">
      {walletOptions.map((option) => (
        <div key={option.name} className="flex items-center justify-between rounded-lg border border-border bg-background p-4">
          <div>
            <div className="font-semibold text-foreground">{option.name}</div>
            <div className="text-xs text-muted-foreground mt-1">{option.tag}</div>
          </div>
          {option.recommended && <span className="rounded-md bg-primary/10 px-2.5 py-1 text-[11px] font-mono text-primary">recommended</span>}
        </div>
      ))}
    </div>
    <a href="#wizard" className="mt-6 inline-flex items-center gap-2 text-primary font-semibold">
      Open issuer review wizard <ArrowRight className="w-4 h-4" />
    </a>
    <p className="mt-4 text-xs text-muted-foreground leading-relaxed">Illustrative preview — wallet connection is not live.</p>
  </div>
);

const HowItWorks = () => {
  const [active, setActive] = useState(0);
  const activeStep = howItWorks[active];

  return (
    <section id="workflow" className="py-24 lg:py-32 bg-muted/20 border-t border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <div>
          <div className="section-label mb-4">How It Works</div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight text-foreground">
            Legal document first. Certificate second. <span className="gradient-text">Records always.</span>
          </h2>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed mb-8">
            Tangibl3Net helps issuers move from documents to controlled certificate workflows without skipping evidence, responsible-party review, transfer rules, ledger records, or human review.
          </p>
          <div className="space-y-4">
            {howItWorks.map((step, index) => (
              <StepButton key={step.num} step={step} active={index === active} onClick={() => setActive(index)} />
            ))}
          </div>
        </div>
        <WalletPanel activeStep={activeStep} />
      </div>
    </section>
  );
};

export default HowItWorks;
